<template>
  <modal
    :loading="loading"
    :title="$t('set_label_sets.create_from_filters_title')"
    style="max-width: 1200px"
    @close_modal="close_modal"
  >
    <template v-slot:content>
      <v-text-field
        id="name"
        v-model="label_set_name"
        :label="$t('set_label_sets.edit_name')"
        required
      ></v-text-field>
      <v-text-field
        id="description"
        v-model="label_set_description"
        :label="$t('set_label_sets.edit_description')"
      ></v-text-field>

      <div
        v-if="unique_labels.length > 0"
        id="labels_found_summary"
        class="labels-found-summary"
      >
        {{ $t('set_label_sets.labels_found') }}: {{ unique_labels.length }}
        <span v-if="preview_count > 0 && unique_labels.length > preview_count">
          — {{ $t('set_label_sets.previewing_first', { n: preview_count }) }}
        </span>
      </div>
      
      <div class="row">
        <div class="col-md-3">
          <filter-tree
            ref="filterTree"
            :title="'Filter annotations:'"
            :initial_filter="initial_filter"
            :non_deletable_filter_types="[41]"
            :non_togglable_filter_types="[41]"
            @update="update"
            @load="load"
            :max_results="max_results"
          ></filter-tree>
        </div>
        <div class="col-md-9">
          <div>
            <grid
              v-if="is_grouped"
              @update_selection_asset_ids="update_selection"
              @reload="load"
              :items="grouped_images"
            ></grid>
            <grid
              v-else
              @update_selection_asset_ids="update_selection"
              @reload="load"
              :items="images"
            ></grid>
          </div>
        </div>
      </div>
    </template>
    <template v-slot:buttons>
      <v-btn
        color="primary"
        text
        id="cancel_button"
        @click="close_modal"
      >
        {{ $t('set_label_sets.btn_cancel') }}
      </v-btn>
      <v-btn
        color="primary"
        text
        id="create_button"
        :disabled="disable_create_button || !label_set_name || unique_labels.length === 0"
        @click="createLabelSet"
      >
        {{ $t('set_label_sets.btn_create_labelset') }}
      </v-btn>
    </template>
  </modal>
</template>

<script>
import Modal from "@/components/Modal";
import FilterTree from "@/components/asset_filter/FilterTree";
import Grid from "@/components/Grid";
import { normalizeFilterQuery, toApiFilters } from "@/components/asset_filter/filterQuery";

const PREVIEW_LIMIT_DEFAULT = 12;
const LABELS_COLLECT_LIMIT = 100000;

export default {
  name: "CreateLabelSetFromFiltersModal",
  components: {
    FilterTree,
    Grid,
    Modal,
  },
  data() {
    return {
      loading: false,
      disable_create_button: true,
      filter: false,
      unique_labels: [],
      unique_label_ids: [],
      label_set_name: "",
      label_set_description: "",
      grouped_results: [],
      images: [],
      grouped_images: [],
      max_results: PREVIEW_LIMIT_DEFAULT,
      preview_count: 0,
      is_grouped: false,
      initial_filter: [
        { type: 27, value: "" },
        { type: 9, value: "Yes" },
        { type: 11, value: "No" },
        { type: 61, value: "No" },
        { type: 41, value: "Yes" }, // Always include groupedBy label
      ],
    };
  },
  methods: {
    close_modal() {
      this.$emit("close_modal");
    },
    update_selection() {
      // Not used but required by FilterAnnotationsWithPreview
    },
    loaded() {
      this.disable_create_button = false;
    },
    update_filter(filters) {
      this.filter = filters;
      this.disable_create_button = true;
      this.unique_labels = [];
      this.unique_label_ids = [];
    },
    ensureGroupedBy(filters) {
      const fq = normalizeFilterQuery(filters || []);
      const groups = fq.groups.map((g) => {
        const without = g.filter((f) => Number(f.type) !== 41);
        without.push({ type: 41, value: "Yes" });
        return without;
      });
      if (groups.length === 0) {
        groups.push([{ type: 41, value: "Yes" }]);
      }
      return toApiFilters({ op: fq.op, groups });
    },
    update(filters) {
      this.update_filter(this.ensureGroupedBy(filters));
    },
    previewLimit(limit) {
      const n = parseInt(limit, 10);
      if (!Number.isFinite(n) || n <= 0) {
        return PREVIEW_LIMIT_DEFAULT;
      }
      return Math.min(n, PREVIEW_LIMIT_DEFAULT);
    },
    async loadCropsForItems(items, grouped) {
      const out = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (!item.url || !item.bbox) {
          continue;
        }
        const data = await this.$api.asset.get_crop(item.url, item.bbox, item.asset_id);
        const cropData = data.crops.map((crop) => {
          if (grouped) {
            return {
              label: item.label,
              count: item.count,
              meta: item.meta,
              data: crop.crop,
              certainty: item.certainty,
              bbox_id: item.bbox_id,
              id: item.asset_id,
              annotation_type: item.annotation_type,
              is_grouped: true,
            };
          }
          return {
            asset_id: item.asset_id,
            meta: item.meta,
            data: crop.crop,
            certainty: item.certainty,
            bbox_id: item.bbox_id,
            label: item.label,
            id: item.asset_id,
          };
        });
        out.push(...cropData);
      }
      return out;
    },
    async load(filters, limit) {
      this.update_filter(filters);
      if (!filters) {
        filters = this.filter;
        limit = limit || PREVIEW_LIMIT_DEFAULT;
      }

      filters = this.ensureGroupedBy(filters);
      const previewLimit = this.previewLimit(limit);

      this.images = [];
      this.grouped_images = [];
      this.preview_count = 0;
      this.loading = true;

      try {
        // Preview only — never fetch crops for every matching label.
        let results = await this.$api.annotation.filter(filters, previewLimit);
        this.$emit("loaded");
        let items = results.items || [];
        let max_results = results.max_results;
        let is_grouped = results.is_grouped || false;

        this.max_results = max_results;
        this.is_grouped = is_grouped;

        if (items.length === 0) {
          this.max_results = 0;
          await this.extractUniqueLabels(filters);
          this.loaded();
          return;
        }

        const previewItems = items.slice(0, previewLimit);
        this.preview_count = previewItems.length;

        if (is_grouped) {
          this.grouped_images = await this.loadCropsForItems(previewItems, true);
        } else {
          this.images = await this.loadCropsForItems(previewItems, false);
        }

        await this.extractUniqueLabels(filters);
        this.loaded();
      } catch (error) {
        console.error("Error loading annotations:", error);
      } finally {
        this.loading = false;
      }
    },
    async extractUniqueLabels(filters) {
      if (!filters) {
        filters = this.filter;
      }
      if (!filters) {
        return;
      }

      filters = this.ensureGroupedBy(filters);

      try {
        // Lightweight: label ids/names only — no sample crops.
        let results = await this.$api.annotation.filter(filters, LABELS_COLLECT_LIMIT, {
          labels_only: true,
        });

        if (results.items && results.items.length > 0) {
          const ids = [];
          const names = [];
          const seen = new Set();
          for (const item of results.items) {
            const name = item.label;
            if (!name || seen.has(name)) {
              continue;
            }
            seen.add(name);
            names.push(name);
            if (item.label_id != null) {
              ids.push(item.label_id);
            }
          }
          this.unique_labels = names;
          this.unique_label_ids = ids;
          this.grouped_results = results.items;
          if (typeof results.max_results === "number") {
            this.max_results = results.max_results;
          }
        } else {
          this.unique_labels = [];
          this.unique_label_ids = [];
        }
      } catch (error) {
        console.error("Error extracting labels:", error);
        this.unique_labels = [];
        this.unique_label_ids = [];
      }
    },
    async createLabelSet() {
      if (!this.label_set_name || this.unique_labels.length === 0) {
        return;
      }

      this.loading = true;

      try {
        let labelIds = this.unique_label_ids.slice();
        if (labelIds.length === 0) {
          const resolved = (await this.$api.label.fetchByNames(this.unique_labels)) || [];
          labelIds = resolved.map((l) => l.id);
        }

        if (labelIds.length === 0) {
          await this.$store.dispatch("snackbars/add_error_snack", {
            message: "No valid labels found to add to labelset",
          });
          this.loading = false;
          return;
        }

        const labelSet = await this.$api.label_set.addLabelSet(
          this.label_set_name,
          this.label_set_description
        );

        await this.$api.label_set.updateLabels(labelSet, labelIds);

        await this.$store.dispatch("snackbars/add_success_snack", {
          message: "Labelset created successfully",
        });

        this.$emit("reload");
        this.close_modal();
      } catch (error) {
        console.error("Error creating labelset:", error);
        await this.$store.dispatch("snackbars/add_error_snack", {
          message: error.message || "Failed to create labelset",
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.labels-found-summary {
  margin-bottom: 12px;
  font-weight: 600;
}
</style>
